import axios from 'axios'
import { LocalStorage } from '../services'
import { Alerts } from '../components/Simple'
import CryptoJS from 'crypto-js'

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

export default function ApiHook() {
  // Instância do Axios
  const apiClient = axios.create({
    baseURL: import.meta.env.VITE_URL_API,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  apiClient.interceptors.response.use(
    response => response,
    async error => {
      // console.log('Erro na requisição:', error)

      const originalRequest = error.config

      if (originalRequest?.skipAuthRefresh) {
        return Promise.reject(error)
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject })
          })
            .then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token
              return apiClient(originalRequest)
            })
            .catch(err => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        const refreshToken = LocalStorage.get('user-refreshToken')
        if (!refreshToken) {
          Alerts.swal('warning', 'Aviso', 'Sessão expirada. Faça login novamente.')
          LocalStorage.clear()
          // console.log('Erro: Token de atualização não encontrado.')
          // window.location.href = '/'
          return Promise.reject(error)
        }

        try {
          const { data } = await axios.post(
            `${import.meta.env.VITE_URL_API}/token/refreshTokenDinamico`,
            {
              novoPadrao: true,
              idusuario: LocalStorage.get('user-id'),
            },
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            },
          )

          const bytes = CryptoJS.AES.decrypt(data.accessToken, import.meta.env.VITE_CRYPTO_KEY)
          const newAT = bytes.toString(CryptoJS.enc.Utf8)

          const bytesRefresh = CryptoJS.AES.decrypt(
            data.refreshToken,
            import.meta.env.VITE_CRYPTO_KEY,
          )
          const newRT = bytesRefresh.toString(CryptoJS.enc.Utf8)
          LocalStorage.set('user-token', newAT)
          LocalStorage.set('user-refreshToken', newRT)
          apiClient.defaults.headers['Authorization'] = `Bearer ${newAT}`
          originalRequest.headers['Authorization'] = `Bearer ${newAT}`

          processQueue(null, data.accessToken)
          return apiClient(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          Alerts.swal('error', 'Sessão Expirada', 'Por favor, faça login novamente.')
          LocalStorage.clear()
          // console.log('Erro ao atualizar o token:', refreshError)
          // window.location.href = '/'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(error)
    },
  )

  const getToken = (isPublic, validRefreshToken) => {
    if (isPublic) return import.meta.env.VITE_TOKEN
    return validRefreshToken
      ? LocalStorage.get('user-token')
      : LocalStorage.get('user-refreshToken')
  }

  // Métodos da API
  const apiGET = async (url, isPublic = false) => {
    try {
      const token = getToken(isPublic, true)
      const req = await apiClient.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return req.data
    } catch (e) {
      return { error: true, message: e.message, erro: e }
    }
  }

  const apiPOST = async (url, data, isPublic = false, validRefreshToken = true) => {
    try {
      const token = getToken(isPublic, validRefreshToken)
      const req = await apiClient.post(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        skipAuthRefresh: !validRefreshToken && url.includes('/token/isValidRefreshTokenDinamico'),
      })

      return req.data
    } catch (e) {
      return { error: true, message: e.message, erro: e }
    }
  }

  const apiPUT = async (url, data, isPublic = false) => {
    try {
      const token = getToken(isPublic, true)
      const req = await apiClient.put(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return req.data
    } catch (e) {
      return { error: true, message: e.message, erro: e }
    }
  }

  const apiDELETE = async (url, data, isPublic = false) => {
    try {
      const token = getToken(isPublic, true)
      const req = await apiClient.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      return req.data
    } catch (e) {
      return { error: true, message: e.message, erro: e }
    }
  }

  const apiFormData = async (url, formData, isPublic = false) => {
    try {
      const token = getToken(isPublic, true)
      const req = await apiClient.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      })
      return req.data
    } catch (e) {
      return { error: true, message: e.message, erro: e }
    }
  }

  return { apiGET, apiPOST, apiPUT, apiDELETE, apiFormData }
}
